import type { Backdrop } from "../types.ts";
import { ellipse, hash, INK, mulberry, paint, shade, type Ctx } from "./util.ts";

export const W = 1280;
export const H = 720;
export const GROUND = 612;
/** Arena cell 0..11 → screen x. */
export const cellX = (cell: number) => 150 + cell * 89;

type SpecKind = "chicken" | "duck" | "pigeon" | "human" | "soldier" | "suit" | "toga";

interface Spectator {
  x: number;
  row: number;
  kind: SpecKind;
  color: string;
  skin: string;
  dir: 1 | -1;
  phase: number;
  speed: number;
  flag?: string;
}

interface Scene {
  back(ctx: Ctx): void;
  front(ctx: Ctx): void;
  anim?(ctx: Ctx, now: number, cheer: number): void;
  crowd: { kinds: SpecKind[]; colors: string[]; rows: [number, number]; flags?: string[] };
}

const SKINS = ["#f2c6a0", "#e0a882", "#c68a5c", "#8d5a3b", "#5c3a24", "#f6d5b8"];

function sky(ctx: Ctx, stops: string[], bottom = GROUND): void {
  const g = ctx.createLinearGradient(0, 0, 0, bottom);
  stops.forEach((c, i) => g.addColorStop(i / (stops.length - 1), c));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
}

function cloud(ctx: Ctx, x: number, y: number, s: number, color = "#ffffff"): void {
  ctx.fillStyle = color;
  for (const [dx, dy, r] of [[0, 0, 28], [30, -12, 34], [64, 0, 28], [32, 8, 26]] as const) {
    ellipse(ctx, x + dx * s, y + dy * s, r * s, r * s * 0.85);
    ctx.fill();
  }
}

function floor(ctx: Ctx, top: number, c0: string, c1: string, seed: number, specks: string): void {
  const g = ctx.createLinearGradient(0, top, 0, H);
  g.addColorStop(0, c0);
  g.addColorStop(1, c1);
  ctx.fillStyle = g;
  ctx.fillRect(0, top, W, H - top);
  const r = mulberry(seed);
  ctx.fillStyle = specks;
  for (let i = 0; i < 260; i++) {
    const y = top + r() * (H - top);
    ellipse(ctx, r() * W, y, 1 + r() * 3 * (y / H), 0.6 + r() * 1.4 * (y / H));
    ctx.fill();
  }
}

function wallTop(ctx: Ctx, y: number, h: number, color: string): void {
  ctx.fillStyle = color;
  ctx.fillRect(0, y, W, h);
  ctx.fillStyle = "rgba(0,0,0,0.18)";
  ctx.fillRect(0, y + h - 6, W, 6);
  ctx.strokeStyle = INK;
  ctx.lineWidth = 3;
  ctx.strokeRect(-4, y, W + 8, h);
}

// ---------------------------------------------------------------- scenes

const SCENES: Record<Backdrop, Scene> = {
  barn: {
    back(ctx) {
      sky(ctx, ["#69b9ec", "#a8dcf7", "#e6f6ff"]);
      ctx.save();
      ctx.shadowColor = "#fff2a0";
      ctx.shadowBlur = 50;
      ellipse(ctx, 1110, 110, 52, 52);
      ctx.fillStyle = "#ffe066";
      ctx.fill();
      ctx.restore();
      ctx.fillStyle = "#8cc76a";
      ellipse(ctx, 300, 500, 520, 120);
      ctx.fill();
      ctx.fillStyle = "#77b555";
      ellipse(ctx, 1000, 510, 560, 130);
      ctx.fill();
      // trees
      for (const [x, s] of [[620, 1], [700, 0.8], [1180, 1.1]] as const) {
        ctx.fillStyle = "#6b4325";
        ctx.fillRect(x - 6 * s, 380, 12 * s, 100);
        ellipse(ctx, x, 370, 44 * s, 52 * s);
        paint(ctx, "#4f9a3a");
      }
      // barn
      ctx.beginPath();
      ctx.moveTo(60, 300);
      ctx.lineTo(120, 200);
      ctx.lineTo(230, 150);
      ctx.lineTo(340, 200);
      ctx.lineTo(400, 300);
      ctx.closePath();
      paint(ctx, "#6b2a22", 4);
      ctx.beginPath();
      ctx.rect(80, 290, 300, 220);
      paint(ctx, "#bf3b2c", 4);
      ctx.strokeStyle = "rgba(0,0,0,0.15)";
      ctx.lineWidth = 2;
      for (let x = 100; x < 380; x += 20) {
        ctx.beginPath();
        ctx.moveTo(x, 292);
        ctx.lineTo(x, 508);
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.rect(170, 390, 120, 120);
      paint(ctx, "#a32f22", 4);
      ctx.strokeStyle = "#f4ead8";
      ctx.lineWidth = 7;
      ctx.strokeRect(173, 393, 114, 114);
      ctx.beginPath();
      ctx.moveTo(173, 393);
      ctx.lineTo(287, 507);
      ctx.moveTo(287, 393);
      ctx.lineTo(173, 507);
      ctx.stroke();
      ctx.beginPath();
      ctx.rect(204, 220, 52, 50);
      paint(ctx, "#2a1a10", 4);
      ctx.fillStyle = "#e8c66a";
      ctx.fillRect(208, 248, 44, 18);
      // silo
      ctx.beginPath();
      ctx.rect(420, 230, 74, 280);
      paint(ctx, "#9fb2bf", 4);
      ctx.beginPath();
      ctx.arc(457, 232, 37, Math.PI, 0);
      paint(ctx, "#7d8f9c", 4);
      ctx.strokeStyle = "rgba(0,0,0,0.15)";
      for (let y = 260; y < 510; y += 28) {
        ctx.beginPath();
        ctx.moveTo(422, y);
        ctx.lineTo(492, y);
        ctx.stroke();
      }
    },
    anim(ctx, now) {
      for (const [x0, y, s, v] of [[100, 90, 1, 9], [600, 140, 0.7, 6], [900, 70, 0.9, 11]] as const) {
        cloud(ctx, ((x0 + now * v) % (W + 300)) - 200, y, s);
      }
    },
    front(ctx) {
      floor(ctx, 540, "#c99f5f", "#9f7540", 11, "rgba(90,60,30,0.35)");
      ctx.strokeStyle = "#e9c45c";
      ctx.lineWidth = 2;
      const r = mulberry(4);
      for (let i = 0; i < 70; i++) {
        const x = r() * W;
        const y = 560 + r() * 160;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + 8 + r() * 10, y - 3 + r() * 6);
        ctx.stroke();
      }
      for (let x = 10; x < W; x += 92) {
        ctx.beginPath();
        ctx.rect(x, 478, 16, 66);
        paint(ctx, "#efe3c8", 3);
      }
      for (const y of [492, 520]) {
        ctx.beginPath();
        ctx.rect(-4, y, W + 8, 12);
        paint(ctx, "#f6ecd6", 3);
      }
    },
    crowd: { kinds: ["chicken", "chicken", "chicken", "duck"], colors: ["#f5f1e6", "#a64b2a", "#2b2b33", "#e8b04a", "#7a4a2a", "#c9853a"], rows: [478, 500] },
  },

  fair: {
    back(ctx) {
      sky(ctx, ["#2b1d4f", "#8a3b6b", "#f08a5d", "#ffd27a"]);
      ctx.fillStyle = "#3a2448";
      ctx.beginPath();
      ctx.moveTo(0, 470);
      for (let x = 0; x <= W; x += 40) ctx.lineTo(x, 440 - Math.abs(Math.sin(x * 0.013)) * 50 - Math.abs(Math.sin(x * 0.041)) * 18);
      ctx.lineTo(W, 520);
      ctx.lineTo(0, 520);
      ctx.fill();
    },
    anim(ctx, now) {
      // Ferris wheel
      const cx = 1020;
      const cy = 260;
      const R = 170;
      ctx.strokeStyle = INK;
      ctx.lineWidth = 8;
      ctx.beginPath();
      ctx.moveTo(cx - 90, 520);
      ctx.lineTo(cx, cy);
      ctx.lineTo(cx + 90, 520);
      ctx.stroke();
      ctx.strokeStyle = "#f2e6d0";
      ctx.lineWidth = 4;
      ctx.stroke();
      const rot = now * 0.15;
      for (let i = 0; i < 12; i++) {
        const a = rot + (i / 12) * Math.PI * 2;
        ctx.strokeStyle = "#f2e6d0";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + Math.cos(a) * R, cy + Math.sin(a) * R);
        ctx.stroke();
      }
      ellipse(ctx, cx, cy, R, R);
      ctx.lineWidth = 6;
      ctx.strokeStyle = "#f2e6d0";
      ctx.stroke();
      for (let i = 0; i < 12; i++) {
        const a = rot + (i / 12) * Math.PI * 2;
        const gx = cx + Math.cos(a) * R;
        const gy = cy + Math.sin(a) * R;
        ctx.beginPath();
        ctx.roundRect(gx - 14, gy, 28, 22, 6);
        paint(ctx, ["#e94f4f", "#4fb0e9", "#f5c431", "#6bd36b"][i % 4] ?? "#fff", 2.5);
        const lit = Math.sin(now * 5 + i) > 0;
        ellipse(ctx, cx + Math.cos(a) * R, cy + Math.sin(a) * R, 3.5, 3.5);
        ctx.fillStyle = lit ? "#fff7b0" : "#a88a50";
        ctx.fill();
      }
      ellipse(ctx, cx, cy, 14, 14);
      paint(ctx, "#f5c431");
      // Tents
      for (const [x, w, h, col] of [[140, 230, 200, "#d6333a"], [420, 180, 160, "#2f6fd6"]] as const) {
        const top = 520 - h;
        ctx.beginPath();
        ctx.rect(x - w / 2, top + h * 0.45, w, h * 0.55);
        paint(ctx, "#f4ead8", 4);
        ctx.save();
        ctx.clip();
        ctx.fillStyle = col;
        for (let sx = x - w / 2; sx < x + w / 2; sx += 36) ctx.fillRect(sx, top, 18, h);
        ctx.restore();
        ctx.beginPath();
        ctx.moveTo(x - w / 2 - 14, top + h * 0.47);
        ctx.lineTo(x, top);
        ctx.lineTo(x + w / 2 + 14, top + h * 0.47);
        ctx.closePath();
        paint(ctx, col, 4);
        ctx.beginPath();
        ctx.moveTo(x, top);
        ctx.lineTo(x, top - 30);
        ctx.lineTo(x + 26, top - 22 + Math.sin(now * 6) * 3);
        ctx.lineTo(x, top - 14);
        ctx.fillStyle = "#f5c431";
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x - 22, 520);
        ctx.lineTo(x, top + h * 0.6);
        ctx.lineTo(x + 22, 520);
        ctx.closePath();
        paint(ctx, "#2a1a10", 3);
      }
      // String lights
      for (let k = 0; k < 2; k++) {
        const at = (i: number) => 90 + k * 50 - Math.sin((i / 26) * Math.PI * 3) * 24;
        ctx.strokeStyle = INK;
        ctx.lineWidth = 2;
        ctx.beginPath();
        for (let i = 0; i <= 26; i++) ctx.lineTo((i / 26) * W, at(i));
        ctx.stroke();
        for (let i = 0; i <= 26; i++) {
          ellipse(ctx, (i / 26) * W, at(i) + 6, 5, 7);
          const on = Math.sin(now * 4 + i * 1.7 + k) > -0.3;
          ctx.fillStyle = on ? (["#ffef8a", "#ff8a8a", "#8ad7ff", "#a6ff8a"][i % 4] ?? "#fff") : "#6a5a3a";
          ctx.fill();
        }
      }
    },
    front(ctx) {
      floor(ctx, 540, "#d9b26f", "#b88a4a", 21, "rgba(110,70,30,0.35)");
      for (let x = -20; x < W; x += 120) {
        ctx.beginPath();
        ctx.roundRect(x, 492, 112, 52, 10);
        paint(ctx, "#e8c66a", 3);
        ctx.strokeStyle = "#c9a24a";
        ctx.lineWidth = 2;
        for (let y = 502; y < 540; y += 9) {
          ctx.beginPath();
          ctx.moveTo(x + 6, y);
          ctx.lineTo(x + 106, y);
          ctx.stroke();
        }
      }
    },
    crowd: { kinds: ["human", "human", "chicken", "duck", "human"], colors: ["#e94f4f", "#4fb0e9", "#f5c431", "#6bd36b", "#f5f1e6", "#a64b2a", "#7a4a2a"], rows: [470, 494] },
  },

  city: {
    back(ctx) {
      sky(ctx, ["#090d22", "#1d2350", "#4a3a6e"]);
      const r = mulberry(7);
      ctx.fillStyle = "#fff";
      for (let i = 0; i < 90; i++) {
        ellipse(ctx, r() * W, r() * 300, r() * 1.4 + 0.3, r() * 1.4 + 0.3);
        ctx.fill();
      }
      ctx.save();
      ctx.shadowColor = "#fffbe0";
      ctx.shadowBlur = 40;
      ellipse(ctx, 180, 110, 44, 44);
      ctx.fillStyle = "#fff6d6";
      ctx.fill();
      ctx.restore();
      for (const [layer, col, minH, maxH] of [[0, "#1b1e3a", 160, 330], [1, "#262a4d", 110, 260]] as const) {
        let x = -20;
        while (x < W) {
          const w = 60 + r() * 90;
          const h = minH + r() * (maxH - minH);
          const top = 520 - h;
          ctx.fillStyle = col;
          ctx.fillRect(x, top, w, h);
          if (layer === 1) {
            ctx.strokeStyle = INK;
            ctx.lineWidth = 3;
            ctx.strokeRect(x, top, w, h);
          }
          for (let wy = top + 12; wy < 500; wy += 20) {
            for (let wx = x + 10; wx < x + w - 12; wx += 18) {
              if (r() < (layer ? 0.45 : 0.3)) {
                ctx.fillStyle = r() < 0.85 ? "#ffd86b" : "#9fd3ff";
                ctx.globalAlpha = layer ? 0.9 : 0.5;
                ctx.fillRect(wx, wy, 8, 11);
                ctx.globalAlpha = 1;
              }
            }
          }
          x += w + 4;
        }
      }
      // Billboard
      ctx.fillStyle = "#3a3a3a";
      ctx.fillRect(760, 330, 10, 140);
      ctx.fillRect(1010, 330, 10, 140);
      ctx.beginPath();
      ctx.rect(700, 190, 380, 150);
      paint(ctx, "#b3261e", 4);
      ctx.beginPath();
      ctx.moveTo(735, 230);
      ctx.lineTo(805, 230);
      ctx.lineTo(795, 310);
      ctx.lineTo(745, 310);
      ctx.closePath();
      paint(ctx, "#fff", 3);
      ctx.fillStyle = "#b3261e";
      ctx.fillRect(750, 250, 40, 8);
      ctx.fillRect(748, 270, 44, 8);
      for (const [x, y] of [[750, 225], [770, 218], [790, 226]] as const) {
        ellipse(ctx, x, y, 12, 10);
        paint(ctx, "#d9902a", 2.5);
      }
      ctx.font = '38px "Luckiest Guy", Impact, sans-serif';
      ctx.fillStyle = "#fff";
      ctx.strokeStyle = INK;
      ctx.lineWidth = 5;
      ctx.textAlign = "left";
      ctx.strokeText("CLUCKY'S", 830, 250);
      ctx.fillText("CLUCKY'S", 830, 250);
      ctx.font = '21px "Luckiest Guy", Impact, sans-serif';
      ctx.fillStyle = "#ffd86b";
      ctx.strokeText("FRIED CHICKEN", 830, 285);
      ctx.fillText("FRIED CHICKEN", 830, 285);
      ctx.font = "14px sans-serif";
      ctx.fillStyle = "#fff";
      ctx.fillText("Finger lickin' evil", 832, 315);
    },
    anim(ctx, now) {
      const on = Math.sin(now * 7) > -0.7 || Math.sin(now * 23) > 0.4;
      ctx.save();
      ctx.font = '30px "Luckiest Guy", Impact, sans-serif';
      ctx.textAlign = "center";
      if (on) {
        ctx.shadowColor = "#ff4fd8";
        ctx.shadowBlur = 24;
      }
      ctx.fillStyle = on ? "#ff9cf0" : "#5a3a5a";
      ctx.fillText("FIGHT CLUB", 410, 300);
      ctx.restore();
    },
    front(ctx) {
      floor(ctx, 540, "#4a4a55", "#2c2c34", 31, "rgba(0,0,0,0.3)");
      ctx.fillStyle = "#e8d36c";
      for (let x = 20; x < W; x += 140) ctx.fillRect(x, 680, 80, 8);
      ellipse(ctx, 1100, 640, 50, 12);
      paint(ctx, "#3a3a40", 3);
      for (let x = -30; x < W; x += 150) {
        ctx.beginPath();
        ctx.rect(x, 498, 140, 26);
        paint(ctx, "#f4f4f4", 3);
        ctx.save();
        ctx.clip();
        ctx.fillStyle = "#e05a1a";
        for (let sx = x - 30; sx < x + 160; sx += 28) {
          ctx.beginPath();
          ctx.moveTo(sx, 524);
          ctx.lineTo(sx + 14, 498);
          ctx.lineTo(sx + 28, 498);
          ctx.lineTo(sx + 14, 524);
          ctx.fill();
        }
        ctx.restore();
        ctx.fillStyle = "#555";
        ctx.fillRect(x + 12, 524, 8, 20);
        ctx.fillRect(x + 120, 524, 8, 20);
      }
    },
    crowd: { kinds: ["pigeon", "human", "pigeon", "human", "human"], colors: ["#3a3a48", "#6a4a8a", "#2e7d32", "#b3261e", "#8a93a3", "#444"], rows: [478, 500] },
  },

  capitol: {
    back(ctx) {
      sky(ctx, ["#7fb8e6", "#bcdcf4", "#eef6fc"]);
      cloud(ctx, 120, 120, 1.1);
      cloud(ctx, 980, 90, 0.9);
      const cx = 640;
      ctx.beginPath();
      ctx.rect(cx - 380, 330, 760, 190);
      paint(ctx, "#f2efe6", 4);
      ctx.beginPath();
      ctx.moveTo(cx - 400, 335);
      ctx.lineTo(cx + 400, 335);
      ctx.lineTo(cx + 380, 310);
      ctx.lineTo(cx - 380, 310);
      ctx.closePath();
      paint(ctx, "#e4dfd0", 4);
      for (let x = cx - 360; x <= cx + 360; x += 40) {
        ctx.beginPath();
        ctx.rect(x - 7, 345, 14, 165);
        paint(ctx, "#fbfaf4", 2);
      }
      ctx.beginPath();
      ctx.moveTo(cx - 140, 310);
      ctx.lineTo(cx, 255);
      ctx.lineTo(cx + 140, 310);
      ctx.closePath();
      paint(ctx, "#ece7da", 4);
      ctx.beginPath();
      ctx.rect(cx - 120, 180, 240, 80);
      paint(ctx, "#f2efe6", 4);
      for (let x = cx - 105; x <= cx + 105; x += 21) {
        ctx.fillStyle = "#d8d2c0";
        ctx.fillRect(x - 4, 190, 8, 64);
      }
      ctx.beginPath();
      ctx.arc(cx, 182, 118, Math.PI, 0);
      ctx.closePath();
      paint(ctx, "#f7f4ec", 4);
      ctx.strokeStyle = "#d8d2c0";
      ctx.lineWidth = 3;
      for (let i = 1; i < 6; i++) {
        ctx.beginPath();
        ctx.ellipse(cx, 182, 118 * (i / 6), 118, 0, Math.PI, 0);
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.rect(cx - 16, 40, 32, 30);
      paint(ctx, "#f2efe6", 3);
      ellipse(ctx, cx, 40, 16, 12);
      paint(ctx, "#f2efe6", 3);
      ctx.fillStyle = "#f5c431";
      ellipse(ctx, cx, 24, 7, 9);
      ctx.fill();
      ctx.beginPath();
      ctx.rect(cx - 30, 420, 60, 100);
      paint(ctx, "#5a4632", 3);
      for (const x of [140, 1140]) {
        ellipse(ctx, x, 440, 70, 80);
        paint(ctx, "#4f8f3a", 3);
      }
    },
    anim(ctx, now, cheer) {
      for (const x of [300, 980]) {
        ctx.fillStyle = "#8a8a8a";
        ctx.fillRect(x - 3, 220, 6, 300);
        ctx.beginPath();
        ctx.moveTo(x + 3, 230);
        for (let i = 0; i <= 10; i++) ctx.lineTo(x + 3 + i * 9, 230 + Math.sin(now * 5 + i * 0.6) * 5 * (i / 10));
        for (let i = 10; i >= 0; i--) ctx.lineTo(x + 3 + i * 9, 284 + Math.sin(now * 5 + i * 0.6) * 5 * (i / 10));
        ctx.closePath();
        paint(ctx, "#f5c431", 2.5);
        ellipse(ctx, x + 48, 257 + Math.sin(now * 5 + 3) * 3, 12, 15);
        paint(ctx, "#fff6dc", 2);
      }
      // Press camera flashes
      const r = mulberry(Math.floor(now * 6));
      if (r() < 0.25 + cheer * 0.4) {
        const fx = r() * W;
        ctx.save();
        ctx.globalAlpha = 0.85;
        ctx.fillStyle = "#fff";
        ctx.shadowColor = "#fff";
        ctx.shadowBlur = 30;
        ellipse(ctx, fx, 470, 8, 8);
        ctx.fill();
        ctx.restore();
      }
    },
    front(ctx) {
      floor(ctx, 540, "#ece6d6", "#cfc6b0", 41, "rgba(120,100,70,0.15)");
      ctx.strokeStyle = "rgba(120,100,70,0.25)";
      ctx.lineWidth = 2;
      for (let y = 560; y < H; y += 40) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(W, y);
        ctx.stroke();
      }
      for (let x = -400; x < W + 400; x += 80) {
        ctx.beginPath();
        ctx.moveTo(x, 540);
        ctx.lineTo(x + (x - 640) * 0.6, H);
        ctx.stroke();
      }
      for (let x = 30; x < W; x += 160) {
        ctx.beginPath();
        ctx.rect(x - 5, 500, 10, 44);
        paint(ctx, "#d4a017", 2.5);
        ellipse(ctx, x, 498, 8, 8);
        paint(ctx, "#f5c431", 2.5);
        ctx.beginPath();
        ctx.moveTo(x + 5, 508);
        ctx.quadraticCurveTo(x + 80, 532, x + 155, 508);
        ctx.strokeStyle = INK;
        ctx.lineWidth = 9;
        ctx.stroke();
        ctx.strokeStyle = "#a3162f";
        ctx.lineWidth = 6;
        ctx.stroke();
      }
    },
    crowd: { kinds: ["suit", "suit", "soldier", "human"], colors: ["#1f2a44", "#2c2c3a", "#5a5a6a", "#3b4a6b", "#7a1f3d"], rows: [470, 494] },
  },

  summit: {
    back(ctx) {
      sky(ctx, ["#1a0610", "#5a0f1e", "#b8331f", "#f08a3a"]);
      ctx.fillStyle = "#2a0e14";
      ctx.beginPath();
      ctx.moveTo(0, 520);
      const pts = [[0, 380], [120, 300], [220, 360], [340, 250], [470, 370], [560, 330], [700, 400], [820, 280], [960, 360], [1080, 270], [1200, 350], [1280, 320]] as const;
      for (const [x, y] of pts) ctx.lineTo(x, y);
      ctx.lineTo(W, 520);
      ctx.fill();
      ctx.fillStyle = "#f4e6e0";
      for (const [x, y] of [[340, 250], [820, 280], [1080, 270]] as const) {
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x - 30, y + 36);
        ctx.lineTo(x + 30, y + 36);
        ctx.fill();
      }
    },
    anim(ctx, now) {
      const cx = 640;
      const cy = 300;
      const R = 200;
      ctx.save();
      ctx.shadowColor = "#ff8a3a";
      ctx.shadowBlur = 60;
      ellipse(ctx, cx, cy, R, R);
      const g = ctx.createRadialGradient(cx - 60, cy - 70, 20, cx, cy, R);
      g.addColorStop(0, "#5ab4f0");
      g.addColorStop(1, "#14407a");
      ctx.fillStyle = g;
      ctx.fill();
      ctx.restore();
      ctx.save();
      ellipse(ctx, cx, cy, R, R);
      ctx.clip();
      const r = mulberry(99);
      const lands: [number, number, number, number][] = [];
      for (let i = 0; i < 9; i++) lands.push([r() * 2 * R * 2, (r() - 0.5) * R * 1.4, 40 + r() * 70, 30 + r() * 60]);
      const shift = (now * 18) % (R * 4);
      for (const [lx, ly, rx, ry] of lands) {
        for (const off of [0, -R * 4]) {
          const x = cx - R * 2 + ((lx + shift) % (R * 4)) + off + R;
          ellipse(ctx, x, cy + ly, rx, ry, 0.4);
          ctx.fillStyle = "#4f9a3a";
          ctx.fill();
        }
      }
      // Gold tint: the world is already half-conquered.
      ctx.fillStyle = "rgba(245,196,49,0.25)";
      ctx.fillRect(cx - R, cy - R, R * 2, R * 2);
      ctx.restore();
      ellipse(ctx, cx, cy, R, R);
      ctx.lineWidth = 5;
      ctx.strokeStyle = INK;
      ctx.stroke();
      // banners
      for (const x of [140, 1140]) {
        ctx.beginPath();
        ctx.moveTo(x - 60, 40);
        ctx.lineTo(x + 60, 40);
        ctx.lineTo(x + 60 + Math.sin(now * 2) * 4, 400);
        ctx.lineTo(x, 360);
        ctx.lineTo(x - 60 + Math.sin(now * 2 + 1) * 4, 400);
        ctx.closePath();
        paint(ctx, "#a3162f", 4);
        ellipse(ctx, x, 170, 32, 42);
        paint(ctx, "#f5c431", 3);
        ctx.font = '34px "Luckiest Guy", Impact, sans-serif';
        ctx.textAlign = "center";
        ctx.fillStyle = "#a3162f";
        ctx.fillText("C", x, 184);
      }
    },
    front(ctx) {
      floor(ctx, 540, "#4a3a3a", "#251b1d", 51, "rgba(0,0,0,0.3)");
      ellipse(ctx, 640, 640, 360, 60);
      ctx.strokeStyle = "#d4a017";
      ctx.lineWidth = 5;
      ctx.stroke();
      ellipse(ctx, 640, 640, 300, 46);
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.beginPath();
      ctx.rect(-4, 500, W + 8, 44);
      paint(ctx, "#6a5650", 3);
      for (let x = 10; x < W; x += 40) {
        ctx.beginPath();
        ctx.roundRect(x, 508, 18, 30, 8);
        paint(ctx, "#8a7670", 2);
      }
    },
    crowd: { kinds: ["human", "suit", "human", "soldier"], colors: ["#d32f2f", "#1f4fa3", "#f5c431", "#2e7d32", "#f4f4f4", "#111"], rows: [470, 494], flags: ["#d32f2f", "#1f4fa3", "#f5c431", "#2e7d32", "#ffffff"] },
  },

  colosseum: {
    back(ctx) {
      sky(ctx, ["#f2a65a", "#f8cf8a", "#fdebc4"]);
      cloud(ctx, 200, 90, 0.9, "#fff4e0");
      cloud(ctx, 860, 60, 1.1, "#fff4e0");
      for (let tier = 0; tier < 3; tier++) {
        const y = 140 + tier * 120;
        const h = 120;
        ctx.beginPath();
        ctx.rect(-4, y, W + 8, h);
        paint(ctx, shade("#d9b98a", -tier * 0.04), 4);
        for (let x = 20 - tier * 30; x < W; x += 88) {
          ctx.beginPath();
          ctx.moveTo(x, y + h);
          ctx.lineTo(x, y + 40);
          ctx.arc(x + 30, y + 40, 30, Math.PI, 0);
          ctx.lineTo(x + 60, y + h);
          ctx.closePath();
          paint(ctx, "#5a3f2a", 3);
          ctx.fillStyle = "#c8a575";
          ctx.fillRect(x + 64, y + 10, 20, h - 10);
        }
      }
    },
    anim(ctx, now) {
      for (let x = 60; x < W; x += 230) {
        ctx.beginPath();
        ctx.moveTo(x, 150);
        ctx.lineTo(x + 50, 150);
        ctx.lineTo(x + 50 + Math.sin(now * 3 + x) * 3, 240);
        ctx.lineTo(x + 25, 222);
        ctx.lineTo(x + Math.sin(now * 3 + x + 1) * 3, 240);
        ctx.closePath();
        paint(ctx, x % 460 === 60 ? "#a3162f" : "#2f4fa3", 3);
        ellipse(ctx, x + 25, 186, 10, 13);
        paint(ctx, "#f5c431", 2);
      }
    },
    front(ctx) {
      floor(ctx, 540, "#e9cc92", "#c9a466", 61, "rgba(140,100,50,0.3)");
      wallTop(ctx, 494, 50, "#c8a575");
      ctx.fillStyle = "rgba(90,63,42,0.35)";
      for (let x = 0; x < W; x += 64) ctx.fillRect(x, 494, 3, 50);
    },
    crowd: { kinds: ["toga", "chicken", "toga", "chicken", "human"], colors: ["#f4f0e6", "#a64b2a", "#2b2b33", "#e8b04a", "#7a1f3d", "#2f4fa3"], rows: [470, 492] },
  },
};

// ---------------------------------------------------------------- crowd

function crowdFor(kind: Backdrop): Spectator[] {
  const sc = SCENES[kind];
  const r = mulberry(hash(kind));
  const list: Spectator[] = [];
  for (let row = 0; row < 2; row++) {
    for (let x = -10 + row * 22; x < W + 20; x += 38 + r() * 14) {
      list.push({
        x,
        row,
        kind: sc.crowd.kinds[Math.floor(r() * sc.crowd.kinds.length)] ?? "chicken",
        color: sc.crowd.colors[Math.floor(r() * sc.crowd.colors.length)] ?? "#fff",
        skin: SKINS[Math.floor(r() * SKINS.length)] ?? "#f2c6a0",
        dir: r() < 0.5 ? 1 : -1,
        phase: r() * 10,
        speed: 6 + r() * 5,
        flag: sc.crowd.flags && r() < 0.3 ? sc.crowd.flags[Math.floor(r() * sc.crowd.flags.length)] : undefined,
      });
    }
  }
  return list;
}

function drawSpectator(ctx: Ctx, s: Spectator, y: number, now: number, cheer: number): void {
  const bounce = Math.abs(Math.sin(now * s.speed * (0.5 + cheer * 0.6) + s.phase));
  const by = y - bounce * (1.5 + cheer * 9);
  const d = s.dir;
  ctx.save();
  ctx.translate(s.x, by);
  if (s.row === 0) ctx.scale(0.85, 0.85);
  const bird = s.kind === "chicken" || s.kind === "duck" || s.kind === "pigeon";
  if (bird) {
    const body = s.kind === "pigeon" ? "#8a93a3" : s.color;
    ellipse(ctx, 0, 14, 18, 15);
    paint(ctx, body, 2.5);
    ellipse(ctx, d * 5, -4, 10, 10);
    paint(ctx, s.kind === "pigeon" ? "#5b6d8a" : body, 2.5);
    if (s.kind === "chicken") {
      ctx.beginPath();
      ctx.moveTo(d * -2, -12);
      ctx.quadraticCurveTo(d * 2, -22, d * 6, -13);
      ctx.quadraticCurveTo(d * 9, -20, d * 11, -11);
      paint(ctx, "#d8342c", 2);
    }
    ctx.beginPath();
    const beakLen = s.kind === "duck" ? 12 : 8;
    ctx.moveTo(d * 13, -7);
    ctx.lineTo(d * (13 + beakLen), -3 + (cheer > 0.4 ? bounce * 2 : 0));
    ctx.lineTo(d * 13, 0);
    ctx.closePath();
    paint(ctx, s.kind === "duck" ? "#f08a24" : s.kind === "pigeon" ? "#333" : "#f2b134", 2);
    ellipse(ctx, d * 8, -6, 1.8, 1.8);
    ctx.fillStyle = INK;
    ctx.fill();
    if (cheer > 0.5) {
      ctx.save();
      ctx.translate(-d * 8, 8);
      ctx.rotate(-d * (1.2 + bounce * 0.6));
      ellipse(ctx, 10, 0, 12, 5);
      paint(ctx, shade(body, -0.1), 2);
      ctx.restore();
    }
  } else {
    const shirt = s.kind === "soldier" ? "#4f5a2e" : s.kind === "toga" ? "#f4f0e6" : s.color;
    if (cheer > 0.45) {
      ctx.strokeStyle = INK;
      ctx.lineWidth = 7;
      ctx.lineCap = "round";
      const arm = bounce * 6;
      ctx.beginPath();
      ctx.moveTo(-12, 8);
      ctx.lineTo(-18, -20 - arm);
      ctx.moveTo(12, 8);
      ctx.lineTo(18, -20 - arm);
      ctx.stroke();
      ctx.strokeStyle = shirt;
      ctx.lineWidth = 4;
      ctx.stroke();
      ctx.lineCap = "butt";
      if (s.flag) {
        ctx.fillStyle = s.flag;
        ctx.fillRect(18, -46 - arm, 22, 15);
        ctx.strokeStyle = INK;
        ctx.lineWidth = 2;
        ctx.strokeRect(18, -46 - arm, 22, 15);
        ctx.beginPath();
        ctx.moveTo(18, -46 - arm);
        ctx.lineTo(18, -18 - arm);
        ctx.stroke();
      }
    }
    ctx.beginPath();
    ctx.roundRect(-18, 2, 36, 30, 10);
    paint(ctx, shirt, 2.5);
    if (s.kind === "suit") {
      ctx.beginPath();
      ctx.moveTo(-5, 3);
      ctx.lineTo(0, 16);
      ctx.lineTo(5, 3);
      ctx.fillStyle = "#fff";
      ctx.fill();
    }
    ellipse(ctx, 0, -10, 12, 13);
    paint(ctx, s.skin, 2.5);
    if (s.kind === "soldier") {
      ctx.beginPath();
      ctx.arc(0, -13, 14, Math.PI, 0);
      ctx.closePath();
      paint(ctx, "#5a6634", 2.5);
    } else if (s.kind === "toga") {
      ctx.strokeStyle = "#4f8f3a";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(0, -12, 12, Math.PI * 1.05, Math.PI * 1.95);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.arc(0, -13, 12.5, Math.PI * 1.05, Math.PI * 1.95);
      ctx.closePath();
      paint(ctx, shade(s.color, -0.5), 2);
    }
    ctx.fillStyle = INK;
    ellipse(ctx, d * 4, -11, 1.6, 2);
    ctx.fill();
    if (cheer > 0.4) {
      ellipse(ctx, d * 5, -3, 3, 1 + bounce * 2.5);
      ctx.fill();
    }
  }
  ctx.restore();
}

// ---------------------------------------------------------------- public

const layers = new Map<Backdrop, { back: HTMLCanvasElement; front: HTMLCanvasElement; crowd: Spectator[] }>();
let layerDpr = 0;

function layer(dpr: number, paintFn: (ctx: Ctx) => void): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = W * dpr;
  c.height = H * dpr;
  const ctx = c.getContext("2d");
  if (!ctx) throw new Error("2d context unavailable");
  ctx.scale(dpr, dpr);
  paintFn(ctx);
  return c;
}

/** Draws the full arena backdrop: sky, set dressing, crowd, wall and floor. `cheer` is 0..1. */
export function drawArena(ctx: Ctx, kind: Backdrop, now: number, cheer: number): void {
  const dpr = ctx.getTransform().a;
  // Static layers are rasterized at the current resolution; a resize invalidates them.
  if (dpr !== layerDpr) {
    layers.clear();
    layerDpr = dpr;
  }
  let l = layers.get(kind);
  if (!l) {
    const sc = SCENES[kind];
    l = { back: layer(dpr, sc.back), front: layer(dpr, sc.front), crowd: crowdFor(kind) };
    layers.set(kind, l);
  }
  const sc = SCENES[kind];
  ctx.drawImage(l.back, 0, 0, W, H);
  sc.anim?.(ctx, now, cheer);
  for (const s of l.crowd) drawSpectator(ctx, s, sc.crowd.rows[s.row] ?? 480, now, cheer);
  ctx.drawImage(l.front, 0, 0, W, H);
}
